import React, { useState } from "react";

import * as RadioGroup from "@radix-ui/react-radio-group";
import { LabeledControl } from "@replo/design-system/blocks/labeled-control/LabeledControl";
import { Input } from "@replo/design-system/components/input/Input";
import twMerge from "@replo/design-system/utils/twMerge";
import { useCurrentWorkspace } from "@v2/contexts/WorkspaceDashboardContext";
import { useOnboardingCreateProject } from "@v2/features/onboarding/hooks/useOnboardingCreateProject";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { onboardingRouter } from "@v2/trpc/routes/onboarding";
import { projectUtils } from "@v2/trpc/routes/project";
import { userUtils } from "@v2/trpc/routes/user";
import { workspaceRouter, workspaceUtils } from "@v2/trpc/routes/workspace";
import { routes } from "@v2/utils/router";
import { useNavigate, useSearchParams } from "react-router-dom";

import OnboardingFooter from "./OnboardingFooter";

const getDefaultProjectName = (fullName: string): string => {
  return `${fullName}'s Shop`;
};

const OnboardingUserInfo: React.FC = () => {
  const logEvent = useLogAnalytics();
  const { user } = useCurrentUser();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pendingAppInstallationId = searchParams.get("pendingAppInstallationId");
  const shopifyIntegrationId = searchParams.get("shopifyIntegrationId");
  const workspace = useCurrentWorkspace();

  const pendingTemplateId = sessionStorage.getItem("replo_pending_template_id");
  const hasPendingTemplate = Boolean(pendingTemplateId);

  const [firstName, setFirstName] = useState(user?.firstName ?? "");
  const [lastName, setLastName] = useState(user?.lastName ?? "");
  const [teamDescription, setTeamDescription] = useState(
    user?.profileType ?? "",
  );
  const [isUpdatingUserInfo, setIsUpdatingUserInfo] = useState(false);
  const [isWaitingForShopifyConnection, setIsWaitingForShopifyConnection] =
    useState(false);
  const isShopifyPaymentProcessor = workspace?.paymentProcessor === "shopify";

  // NOTE (BURK 08/25): We need this state because we don't want to display the black bg when the image is loaded in
  // This is because the image has a fade in/out animation
  const [isBackgroundImageLoaded, setIsBackgroundImageLoaded] = useState(false);

  const { mutateAsync: createWorkspace } = workspaceRouter.create.useMutation();

  const doesUserHaveName = user?.firstName || user?.lastName;
  // Note (Ezra, 2025-11-25): Don't redirect to create-shop if there's a pending template -
  // the handleSubmit logic will auto-create the project and redirect to chat instead
  if (
    doesUserHaveName &&
    !pendingAppInstallationId &&
    !shopifyIntegrationId &&
    !hasPendingTemplate
  ) {
    const currentParams = searchParams.toString();
    const createShopUrl = currentParams
      ? `${routes.onboarding.createShop}?${currentParams}`
      : routes.onboarding.createShop;
    navigate(createShopUrl, { replace: true });
  }

  const { mutateAsync: updateBasicUserInfo } =
    onboardingRouter.updateBasicUserInfo.useMutation();

  const { createProject, isCreatingProject } = useOnboardingCreateProject();

  const handleSubmit = async () => {
    if (teamDescription === "" || !user) {
      return;
    }

    setIsUpdatingUserInfo(true);
    try {
      await updateBasicUserInfo({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        teamDescription,
      });

      await userUtils.get.invalidate();

      logEvent("onboarding.flow.begin", {});
      if (user) {
        logEvent("onboarding.flow.step.completed", {
          userId: user.email,
          stepId: "user-profile",
          stepType: "user-profile",
          stepData: {
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            teamDescription,
          },
        });
      }

      const fullName = [firstName.trim(), lastName.trim()].join(" ").trim();

      // NOTE (Berkley, 2025-10-16): If the user is coming from shopify, we want to create the project here, since they should not see any more onboarding steps
      if (
        isShopifyPaymentProcessor &&
        (pendingAppInstallationId || shopifyIntegrationId)
      ) {
        await createProject({
          shopName: getDefaultProjectName(fullName),
          workspaceId: workspace?.id,
          hostingType: "shopify",
          userEmail: user?.email,
          workspaceName: `${fullName}'s Workspace`,
          shouldInvalidateUser: true,
        });
        setIsWaitingForShopifyConnection(true);
        return;
      }

      // Note (Ezra, 2025-11-25): If user is coming from a template share link, auto-create
      // a v2 project and redirect to home where useApplySharedTemplateAfterAuth will apply the template
      if (hasPendingTemplate) {
        let effectiveWorkspaceId = workspace?.id;

        if (!effectiveWorkspaceId) {
          const newWorkspace = await createWorkspace({
            name: `${firstName.trim()}'s Workspace`,
            paymentProcessor: "stripe",
            eventOrigin: "onboarding",
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          });
          effectiveWorkspaceId = newWorkspace.workspace.id;
          void workspaceUtils.getUserWorkspacesList.invalidate();
          void projectUtils.listGroupedByWorkspaceForUser.invalidate();
        }

        const result = await createProject({
          shopName: getDefaultProjectName(fullName),
          workspaceId: effectiveWorkspaceId,
          hostingType: "replo",
          userEmail: user.email,
          workspaceName: `${fullName}'s Workspace`,
          shouldInvalidateUser: true,
        });

        // Note (Ezra, 2025-11-25): Use hard navigation to ensure fresh data load.
        // useApplySharedTemplateAfterAuth will detect the pending template and apply it.
        if (result.project?.id) {
          window.location.href = `/shops/${result.project.id}/home`;
        } else {
          navigate("/home");
        }
        return;
      }

      const currentParams = searchParams.toString();
      const homeUrl = currentParams ? `/home?${currentParams}` : "/home";
      navigate(homeUrl);
    } finally {
      setIsUpdatingUserInfo(false);
    }
  };

  return (
    <div className="h-screen w-full flex flex-col">
      <div className="flex-1 overflow-y-auto flex flex-col items-center md:py-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <div className=" md:pt-0 my-6 flex flex-col gap-1 text-center md:mb-12 md:gap-3 px-5">
          <h1 className="header-h3 text-2xl md:text-3xl">Welcome to Replo</h1>
          <p className="text-muted header-h3 text-base font-normal">
            Tell us a bit about yourself so we can personalize your experience.
          </p>
        </div>
        <div className="md:w-[640px] w-full flex flex-col gap-8 pb-25 md:pb-40 px-5 md:px-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <LabeledControl label="First Name" size="base">
              <Input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                size="base"
                placeholder="Enter your first name"
                autoFocus
              />
            </LabeledControl>

            <LabeledControl label="Last Name" size="base">
              <Input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                size="base"
                placeholder="Enter your last name"
              />
            </LabeledControl>
          </div>
          <LabeledControl
            label="What best describes you and your team?"
            size="base"
          >
            <RadioGroup.Root
              className="flex flex-col gap-4 h-85"
              value={teamDescription}
              onValueChange={setTeamDescription}
              aria-label="What best describes you and your team?"
            >
              <RadioGroup.Item
                value="business"
                className={twMerge(
                  "px-6 py-4 rounded-xl border-0.5 border-component-border flex-1 flex flex-col justify-center gap-2 relative cursor-pointer shadow-button-selectable text-left",
                  teamDescription === "business" && !isBackgroundImageLoaded
                    ? "bg-black"
                    : "bg-sheet",
                )}
              >
                <img
                  src="/images/onboarding/onboarding-background.png"
                  alt=""
                  onLoad={() => setIsBackgroundImageLoaded(true)}
                  className={twMerge(
                    "absolute inset-0 w-full h-full object-cover rounded-xl transition-opacity duration-300",
                    teamDescription === "business"
                      ? "opacity-100"
                      : "opacity-0",
                  )}
                />
                <div className="pr-35">
                  <h3
                    className={twMerge(
                      "text-base font-semibold font-editorial relative z-10 transition-colors duration-300",
                      teamDescription !== "business" && "text-muted",
                      teamDescription === "business" && "text-brand-seashell",
                    )}
                  >
                    Business
                  </h3>
                  <p
                    className={twMerge(
                      "text-xs relative z-10 transition-colors duration-300",
                      teamDescription !== "business" && "text-muted",
                      teamDescription === "business" && "text-brand-seashell",
                    )}
                  >
                    Grow my business and reach more customers
                  </p>
                </div>
                <img
                  src="/images/onboarding/business-grey.png"
                  alt="Business"
                  className="h-32 w-32 object-contain absolute bottom-[-15px] right-10 z-10"
                />
                <img
                  src="/images/onboarding/business-color.png"
                  alt="Business"
                  className={twMerge(
                    "h-28 w-32 object-contain absolute bottom-[-7px] right-10 z-10 transition-opacity duration-300",
                    teamDescription === "business"
                      ? "opacity-100"
                      : "opacity-0",
                  )}
                />
              </RadioGroup.Item>
              <RadioGroup.Item
                value="agency"
                className={twMerge(
                  "px-6 py-4 rounded-xl border-0.5 border-component-border flex-1 flex flex-col justify-center gap-2 relative cursor-pointer shadow-button-selectable text-left",
                  teamDescription === "agency" && !isBackgroundImageLoaded
                    ? "bg-black"
                    : "bg-sheet",
                )}
              >
                <img
                  src="/images/onboarding/onboarding-background.png"
                  alt=""
                  className={twMerge(
                    "absolute inset-0 w-full h-full object-cover rounded-xl transition-opacity duration-300",
                    teamDescription === "agency" ? "opacity-100" : "opacity-0",
                  )}
                />
                <div className="pr-35">
                  <h3
                    className={twMerge(
                      "text-base font-semibold font-editorial relative z-10 transition-colors duration-300",
                      teamDescription !== "agency" && "text-muted",
                      teamDescription === "agency" && "text-brand-seashell",
                    )}
                  >
                    Agency or Freelancer
                  </h3>
                  <p
                    className={twMerge(
                      "text-xs relative z-10 transition-colors duration-300",
                      teamDescription !== "agency" && "text-muted",
                      teamDescription === "agency" && "text-brand-seashell",
                    )}
                  >
                    I partner with clients to scale their businesses
                  </p>
                </div>
                <img
                  src="/images/onboarding/agency-grey.png"
                  alt="Agency"
                  className="h-30 w-30 object-contain absolute bottom-[-12px] right-12 z-10"
                />
                <img
                  src="/images/onboarding/agency-color.png"
                  alt="Agency"
                  className={twMerge(
                    "h-30 w-30 object-contain absolute bottom-[-20px] right-12 z-10 transition-opacity duration-300",
                    teamDescription === "agency" ? "opacity-100" : "opacity-0",
                  )}
                />
              </RadioGroup.Item>
              <RadioGroup.Item
                value="other"
                className={twMerge(
                  "px-6 py-4 rounded-xl border-0.5 border-component-border flex-1 flex flex-col justify-center gap-2 relative cursor-pointer shadow-button-selectable text-left",
                  teamDescription === "other" && !isBackgroundImageLoaded
                    ? "bg-black"
                    : "bg-sheet",
                )}
              >
                <img
                  src="/images/onboarding/onboarding-background.png"
                  alt=""
                  className={twMerge(
                    "absolute inset-0 w-full h-full object-cover rounded-xl transition-opacity duration-300",
                    teamDescription === "other" ? "opacity-100" : "opacity-0",
                  )}
                />
                <div className="pr-35">
                  <h3
                    className={twMerge(
                      "text-base font-semibold font-editorial relative z-10 transition-colors duration-300",
                      teamDescription !== "other" && "text-muted",
                      teamDescription === "other" && "text-brand-seashell",
                    )}
                  >
                    Just Exploring
                  </h3>
                  <p
                    className={twMerge(
                      "text-xs relative z-10 transition-colors duration-300",
                      teamDescription !== "other" && "text-muted",
                      teamDescription === "other" && "text-brand-seashell",
                    )}
                  >
                    I want to see what I can build with Replo
                  </p>
                </div>
                <img
                  src="/images/onboarding/freelancer-grey.png"
                  alt="Freelancer"
                  className="h-30 w-30 object-contain absolute bottom-[-10px] right-8 z-10"
                />
                <img
                  src="/images/onboarding/freelancer-color.png"
                  alt="Freelancer"
                  className={twMerge(
                    "h-30 w-30 object-contain absolute bottom-[-10px] right-8 z-10 transition-opacity duration-300",
                    teamDescription === "other" ? "opacity-100" : "opacity-0",
                  )}
                />
              </RadioGroup.Item>
            </RadioGroup.Root>
          </LabeledControl>
        </div>
      </div>

      <div className="absolute right-0 bottom-0 left-0 z-20 border-t md:border-t-0 border-border border-[0.5px]">
        <OnboardingFooter
          onNext={() => void handleSubmit()}
          isNextDisabled={
            isUpdatingUserInfo ||
            firstName.trim() === "" ||
            teamDescription === "" ||
            isCreatingProject ||
            isWaitingForShopifyConnection
          }
          className="w-full md:max-w-[640px] px-1 md:px-0"
          isNextLoading={
            isUpdatingUserInfo ||
            isCreatingProject ||
            isWaitingForShopifyConnection
          }
          showBackButton={false}
        />
      </div>
    </div>
  );
};

export default OnboardingUserInfo;
